import { Layout } from '../../components/layout'
import DashboardError from './dashboard-error.jsx'
import { Outlet, useParams } from 'react-router-dom'
import { useAuthSync } from '../../entities/user/api/firebase/subscriptions.js'
import { useUser } from '../../entities/user'
import { useTeam, useTeamPlan, useTeamsByUserId } from '../../entities/team'
import { useRundownsByTeam } from '../../entities/rundown'
import { useEventsByTeamId } from '../../entities/event/api/hooks'
import { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import {
  userAtom,
  userTeamAtom,
  userTeamsAtom,
} from '../../store/user.store.ts'
import { planAtom } from '../../store/plan.store.ts'
import { rundownListAtom } from '../../store/rundownList.store.js'
import { eventListAtom } from '../../store/eventList.store.js'

export default function DashboardParent() {
  const { teamId } = useParams()
  const setUser = useSetAtom(userAtom)
  const setTeam = useSetAtom(userTeamAtom)
  const setTeams = useSetAtom(userTeamsAtom)
  const setPlan = useSetAtom(planAtom)
  const setRundownList = useSetAtom(rundownListAtom)
  const setEventList = useSetAtom(eventListAtom)

  useAuthSync()

  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useUser({
    options: {
      staleTime: Infinity,
    },
  })

  const noRetryOn403 = (failureCount, error) =>
    error?.response?.status !== 403 && failureCount < 3

  const {
    data: team,
    isLoading: isTeamLoading,
    error: teamError,
  } = useTeam({
    params: { teamId },
    options: {
      enabled: !!teamId,
      retry: noRetryOn403,
    },
    hideToast: true,
  })

  const {
    data: teams,
    isLoading: isTeamsLoading,
    error: teamsError,
  } = useTeamsByUserId({
    params: { userId: user?.uid },
    options: {
      enabled: !!user?.uid,
    },
  })

  const {
    data: plan,
    isLoading: isPlanLoading,
    error: planError,
  } = useTeamPlan({
    params: { teamId },
    options: {
      enabled: !!teamId,
      retry: noRetryOn403,
    },
    hideToast: true,
  })

  const {
    data: rundownList,
    isLoading: isRundownsLoading,
    error: rundownsError,
  } = useRundownsByTeam({
    params: { teamId },
    options: {
      enabled: !!teamId,
      retry: noRetryOn403,
    },
    hideToast: true,
  })

  const {
    data: eventList,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useEventsByTeamId({
    params: { teamId },
    options: {
      enabled: !!teamId,
      retry: noRetryOn403,
    },
  })

  const isLoading =
    isUserLoading ||
    isTeamLoading ||
    isTeamsLoading ||
    isPlanLoading ||
    isRundownsLoading ||
    isEventsLoading

  const error =
    userError ||
    teamError ||
    teamsError ||
    planError ||
    rundownsError ||
    eventsError

  // Keep legacy setters for Jotai store
  useEffect(() => {
    setUser(user)
  }, [setUser, user])

  useEffect(() => {
    setTeam(team)
  }, [setTeam, team])

  useEffect(() => {
    setTeams(teams)
  }, [setTeams, teams])

  useEffect(() => {
    setPlan(plan)
  }, [setPlan, plan])

  useEffect(() => {
    setRundownList(rundownList)
  }, [setRundownList, rundownList])

  useEffect(() => {
    setEventList(eventList)
  }, [setEventList, eventList])

  if (error) {
    return <DashboardError error={error} />
  } else if (!isLoading) {
    return (
      <Outlet
        context={{
          user,
          team,
          teams,
          plan,
          rundownList,
          eventList,
        }}
      />
    )
  } else {
    return <DashboardLoader />
  }
}

function DashboardLoader() {
  return (
    <Layout
      title="Loading..."
      loading
    />
  )
}
